// Feature entitlements on the client. Mirrors the proxy's features.js string
// semantics: keys are dot-paths, a parent grants its children, "All" grants
// everything, legacy "CSRD" means reports.frameworks.csrd. The catalogue tree
// itself comes from GET /features (backend-owned); this file only needs keys.

export const ALL = "All";
const LEGACY = { CSRD: "reports.frameworks.csrd" };

export const FEATURES = Object.freeze({
  DASHBOARD: "dashboard",
  DASHBOARD_ENVIRONMENT: "dashboard.environment",
  DASHBOARD_SCOPE1: "dashboard.environment.scope1",
  DASHBOARD_ELECTRICITY: "dashboard.environment.electricity",
  DASHBOARD_TRAVEL_LOGISTICS: "dashboard.environment.travel-logistics",
  DASHBOARD_BUSINESS_TRAVEL: "dashboard.environment.business-travel",
  DASHBOARD_COMMUTE: "dashboard.environment.commute",
  DASHBOARD_TRANSPORT: "dashboard.environment.transport-distribution",
  DASHBOARD_WATER: "dashboard.environment.water",
  DASHBOARD_WASTE: "dashboard.environment.waste",
  DASHBOARD_SOCIAL: "dashboard.social",
  REPORTS: "reports",
  REPORTS_INTERNAL: "reports.internal",
  REPORTS_FRAMEWORKS: "reports.frameworks",
  REPORTS_CSRD: "reports.frameworks.csrd",
  REPORTS_BRSR: "reports.frameworks.brsr",
  GREEN_BUTTON: "green-button",
  FORMS: "forms",
  FORMS_COMMUTE: "forms.commute",
  FORMS_BUSINESS_TRAVEL: "forms.business-travel",
  FORMS_SUPPLIER: "forms.supplier-footprint",
  INSIGHTS: "insights",
  CONFIGURATION: "configuration",
});

export function normaliseFeatures(list) {
  const keys = (Array.isArray(list) ? list : list ? String(list).split(",") : [])
    .map((k) => String(k).trim())
    .filter(Boolean)
    .map((k) => LEGACY[k] || k);
  if (keys.includes(ALL)) return [ALL];
  return [...new Set(keys)];
}

// All, the key, an ancestor — or a descendant: ticking CSRD alone must still
// open the Reports and Standards & Frameworks pages that lead to it.
export function hasFeature(set, key) {
  const granted = normaliseFeatures(set);
  if (grantedDirectly(granted, key)) return true;
  return granted.some((g) => g.startsWith(String(key) + "."));
}

// All, the key itself, or an ancestor — the strict form the picker uses to
// decide which boxes are ticked (a parent is only ticked when it was granted,
// not merely opened by a child).
function grantedDirectly(granted, key) {
  if (granted.includes(ALL)) return true;
  if (!key) return true;
  const parts = String(key).split(".");
  for (let i = parts.length; i >= 1; i--) {
    if (granted.includes(parts.slice(0, i).join("."))) return true;
  }
  return false;
}

// Sidebar page name (as passed to canAccessPage) -> feature key. Pages not
// listed have no feature requirement.
export const PAGE_FEATURE = Object.freeze({
  "esg-dashboard": FEATURES.DASHBOARD,
  "internal-reports": FEATURES.REPORTS_INTERNAL,
  "/standards": FEATURES.REPORTS_FRAMEWORKS,
  comingsoon: FEATURES.GREEN_BUTTON,
  "forms-apps": FEATURES.FORMS,
  insights: FEATURES.INSIGHTS,
  allconfiguration: FEATURES.CONFIGURATION,
  // Company structure and user management pages are role-gated, not plan-gated.
});

// ── Catalogue tree helpers (PrimeReact Tree with checkbox selection) ─────────
// Backend nodes: { key, label, children }. PrimeReact wants the same shape.
// An "All" node (older backends) is dropped: "everything" is every top-level
// module ticked, so Select all / Clear behave as one gesture.
export function toTreeNodes(nodes) {
  return (nodes || [])
    .filter((n) => n.key !== ALL && n.available !== false)
    .map((n) => ({
      key: n.key,
      label: n.label || n.key,
      children: toTreeNodes(n.children),
    }));
}

// Labels of catalogue entries that are on the roadmap but not built ("Water",
// "Green Button", ...) so the picker can say why they aren't offered.
export function comingSoonLabels(nodes, parents = []) {
  const out = [];
  for (const n of nodes || []) {
    if (n.key === ALL) continue;
    if (n.available === false) {
      out.push(parents.length && n.children && n.children.length ? n.label : [...parents, n.label].join(" \u203a "));
      continue; // children are implied
    }
    out.push(...comingSoonLabels(n.children, [...parents, n.label]));
  }
  return out;
}

function walk(nodes, fn, parents = []) {
  for (const n of nodes || []) {
    fn(n, parents);
    walk(n.children, fn, [...parents, n]);
  }
}

// Granted keys -> PrimeReact selectionKeys: every granted node and its
// descendants are checked; ancestors with some checked children are partial.
export function selectionFromKeys(tree, keys) {
  const granted = normaliseFeatures(keys);
  const sel = {};
  if (granted.includes(ALL)) {
    walk(tree, (n) => { sel[n.key] = { checked: true, partialChecked: false }; });
    return sel;
  }
  walk(tree, (n) => {
    if (n.key !== ALL && grantedDirectly(granted, n.key)) sel[n.key] = { checked: true, partialChecked: false };
  });
  // Ancestors: fully checked if all children checked, else partial.
  const mark = (n) => {
    if (!n.children || !n.children.length) return !!(sel[n.key] && sel[n.key].checked);
    const states = n.children.map(mark);
    if (n.key === ALL) return false;
    if (states.every(Boolean)) { sel[n.key] = { checked: true, partialChecked: false }; return true; }
    if (states.some(Boolean) || (sel[n.key] && sel[n.key].checked)) {
      if (!(sel[n.key] && sel[n.key].checked)) sel[n.key] = { checked: false, partialChecked: true };
      return !!(sel[n.key] && sel[n.key].checked);
    }
    return false;
  };
  tree.forEach(mark);
  return sel;
}

// PrimeReact selectionKeys -> the minimal key list to store: a fully-checked
// node stands for its whole subtree; "All" checked collapses to ["All"].
export function keysFromSelection(tree, selection) {
  const sel = selection || {};
  if (sel[ALL] && sel[ALL].checked) return [ALL];
  const out = [];
  const visit = (n) => {
    if (n.key === ALL) return;
    if (sel[n.key] && sel[n.key].checked) { out.push(n.key); return; }
    (n.children || []).forEach(visit);
  };
  (tree || []).forEach(visit);
  // Every top-level module checked == everything.
  const top = (tree || []).filter((n) => n.key !== ALL).map((n) => n.key);
  if (top.length && top.every((k) => out.includes(k))) return [ALL];
  return out;
}

// ESG parameters a TRIAL organization may configure connections for. Product
// decision (Sep 2026): trials get exactly these three "for now"; standard
// plans see the full catalogue. Matching is case-insensitive on the trimmed
// label so backend wording changes don't silently empty the list.
export const TRIAL_ESG_PARAMETERS = Object.freeze([
  "Fuel consumption (Stationary)",
  "Fuel consumption (Mobile)",
  "Purchased Electricity",
]);
const _trialParamSet = new Set(TRIAL_ESG_PARAMETERS.map((p) => p.trim().toLowerCase()));
export function isTrialEsgParameter(label) {
  return _trialParamSet.has(String(label || "").trim().toLowerCase());
}
// Filter a flat list of parameter labels for the current plan.
export function filterEsgParamsForPlan(list, plan) {
  const items = Array.isArray(list) ? list : [];
  if (plan !== "trial") return items;
  return items.filter(isTrialEsgParameter);
}

// A stored trial end ("2026-08-30T00:00:00Z") is a date, not an instant:
// render it in UTC so it never slips a day in western timezones.
export function formatPlanDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

// Human labels for a key list, using the catalogue when available.
export function describeFeatures(keys, tree) {
  const granted = normaliseFeatures(keys);
  if (granted.includes(ALL)) return ["Everything"];
  const labels = {};
  walk(tree || [], (n, parents) => { labels[n.key] = [...parents.map((p) => p.label), n.label].join(" › "); });
  return granted.map((k) => labels[k] || k);
}
